import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Mail, Phone, Users, RefreshCw, GitMerge, Wrench } from 'lucide-react'
import {
  PageHeader, ListToolbar, Button, DataTable, Avatar, Badge, StanceBadge, Card, EmptyState, useViewMode,
  type Column, type ToolbarView, type BadgeTone,
} from '@/components/ui'
import { useStore, useHasPermission } from '@/store/useStore'
import { accountById, contactName, isInternalContact } from '@/lib/selectors'
import type { Account, Contact, ContactType, InfluenceLevel } from '@/types/entities'
import { ContactForm } from './ContactForm'
import { fetchWpiDirectory, planDirectorySync } from '@/auth/directorySync'
import { SyncContactsModal } from './SyncContactsModal'
import { MergeDuplicatesModal } from './MergeDuplicatesModal'
import { DataCorrectionsModal } from './DataCorrectionsModal'
import './ContactsList.css'

// "All Contacts" means all EXTERNAL contacts — the people the CRM exists to
// track. WPI staff live behind their own view: they are colleagues, not
// relationships, and mixing 215 of them into a 1,115-row list buried the
// people anyone was actually looking for (Chauncey 2026-08-26).
const VIEWS: ToolbarView[] = [
  { id: 'all', label: 'All Contacts' },
  { id: 'investor', label: 'Investors' },
  { id: '3pm', label: '3PM' },
  { id: 'internal', label: 'WPI Team' },
]
// Admin-only (crm.manage): archived contacts are stripped server-side for
// everyone else, so this view is appended only for managers.
const ARCHIVED_VIEW: ToolbarView = { id: 'archived', label: 'Archived' }

const TYPE_TONE: Partial<Record<ContactType, BadgeTone>> = {
  Investor: 'navy',
  '3PM': 'info',
  Broker: 'neutral',
  Municipal: 'warn',
  'WPI Employee': 'neutral',
  Vendor: 'neutral',
  Personal: 'neutral',
  JV: 'info',
}

const INFLUENCE_CLASS: Record<InfluenceLevel, string> = {
  High: 'ct-influence-dot--high',
  Medium: 'ct-influence-dot--medium',
  Low: 'ct-influence-dot--low',
}

const INFLUENCE_TONE: Record<InfluenceLevel, BadgeTone> = {
  High: 'info',
  Medium: 'neutral',
  Low: 'neutral',
}

/** Strip a phone string down to a tel:-safe value. */
function telHref(phone: string): string {
  return `tel:${phone.replace(/[^+\d]/g, '')}`
}

function matchesView(c: Contact, view: string): boolean {
  if (view === 'archived') return c.archived === true
  if (c.archived) return false // archived only ever shows in its own view
  if (view === 'internal') return isInternalContact(c)
  // Every other view is external-only — staff appear in 'internal' and
  // nowhere else, including the account rollups and the symposium pickers.
  if (isInternalContact(c)) return false
  if (view === 'all') return true
  if (view === 'investor') return c.type === 'Investor' || c.type === 'JV'
  if (view === '3pm') return c.type === '3PM'
  return true
}

export default function ContactsList() {
  const data = useStore((s) => s.data)
  const upsert = useStore((s) => s.upsert)
  const patchStore = useStore((s) => s.patch)
  const canManage = useHasPermission('crm.manage')
  // Duplicate review is Client Relations' work as much as an admin's — they
  // own the investor contact list and are the people who can verify whether
  // two rows are one person (Chauncey, 2026-09-10). crm.manage still passes so
  // crm_admin never loses it, including in an environment where the new
  // permission is not registered with wise-iam yet.
  const canMerge = useHasPermission('crm.contacts.merge') || canManage
  const nav = useNavigate()
  const [view, setView] = useState('all')
  const [search, setSearch] = useState('')
  const [adding, setAdding] = useState(false)
  const [viewMode, setViewMode] = useViewMode('contacts', 'rows')
  const [syncing, setSyncing] = useState(false)
  // "Sync my contacts" — everyone with crm.use, since it reads only the
  // caller's OWN mailbox and every change is reviewed before it applies.
  const [syncMineOpen, setSyncMineOpen] = useState(false)
  const [syncMsg, setSyncMsg] = useState<string | null>(null)
  const [mergeOpen, setMergeOpen] = useState(false)
  const [correctionsOpen, setCorrectionsOpen] = useState(false)

  // Refresh internal (WPI) contact cards from Entra: titles, departments,
  // work email, phones. Creates cards for staff not in the CRM yet.
  const runDirectorySync = async () => {
    setSyncing(true); setSyncMsg(null)
    try {
      const users = await fetchWpiDirectory()
      const plan = planDirectorySync(users, data.contacts)
      for (const item of plan) {
        if (item.kind === 'create' && item.create) upsert('contacts', item.create)
        else patchStore('contacts', item.contactId, item.patch)
      }
      const created = plan.filter((i) => i.kind === 'create').length
      const updated = plan.length - created
      setSyncMsg(`Directory sync: ${users.length} staff read from Entra — ${created} contact${created === 1 ? '' : 's'} created, ${updated} updated.`)
    } catch (e) {
      setSyncMsg(`Directory sync failed: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setSyncing(false)
    }
  }

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return data.contacts.filter((c) => {
      if (!matchesView(c, view)) return false
      if (!q) return true
      const acct = accountById(data, c.accountId)
      const hay = [
        contactName(c),
        c.title,
        c.department,
        acct?.name,
        c.workEmail,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return hay.includes(q)
    })
  }, [data, view, search])

  const columns: Column<Contact>[] = [
    {
      key: 'name',
      header: 'Name',
      sortValue: (c) => `${c.lastName} ${c.firstName}`.toLowerCase(),
      render: (c) => (
        <span className="ct-name">
          <Avatar name={contactName(c)} size={32} tone={c.isWpi ? 'slate' : 'navy'} />
          <span className="ct-name-text">
            <Link
              to={`/contacts/${c.id}`}
              className="ct-name-link truncate"
              onClick={(e) => e.stopPropagation()}
            >
              {contactName(c)}
            </Link>
          </span>
        </span>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      sortValue: (c) => (c.title ?? '').toLowerCase(),
      render: (c) =>
        c.title ? <span className="ct-cell-strong">{c.title}</span> : <span className="dim">—</span>,
    },
    {
      key: 'account',
      header: 'Account',
      sortValue: (c) => (accountById(data, c.accountId)?.name ?? '').toLowerCase(),
      render: (c) => {
        const acct = accountById(data, c.accountId) as Account | undefined
        if (!acct) return <span className="dim">—</span>
        return (
          <Link to={`/accounts/${acct.id}`} onClick={(e) => e.stopPropagation()}>
            {acct.name}
          </Link>
        )
      },
    },
    {
      key: 'department',
      header: 'Department',
      sortValue: (c) => (c.department ?? '').toLowerCase(),
      render: (c) =>
        c.department ? <span className="ct-cell-muted">{c.department}</span> : <span className="dim">—</span>,
    },
    {
      key: 'type',
      header: 'Type',
      sortValue: (c) => c.type ?? '',
      render: (c) =>
        c.type ? (
          <Badge tone={TYPE_TONE[c.type] ?? 'neutral'} variant="soft">
            {c.type}
          </Badge>
        ) : (
          <span className="dim">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (c) => c.status ?? '',
      render: (c) => (c.status ? <Badge tone="neutral" variant="outline">{c.status}</Badge> : <span className="dim">—</span>),
    },
    {
      key: 'stance',
      header: 'Stance',
      sortValue: (c) => c.stance ?? 'zzz',
      render: (c) => (c.stance ? <StanceBadge stance={c.stance} /> : <span className="dim">—</span>),
    },
    {
      key: 'influence',
      header: 'Influence',
      align: 'left',
      sortValue: (c) => ({ High: 0, Medium: 1, Low: 2 }[c.influence ?? 'Low'] ?? 3),
      render: (c) =>
        c.influence ? (
          <span className="ct-influence">
            <span className={`ct-influence-dot ${INFLUENCE_CLASS[c.influence]}`} />
            {c.influence}
          </span>
        ) : (
          <span className="dim">—</span>
        ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (c) => {
        if (!c.workEmail && !c.cellPhone && !c.officePhone) return <span className="dim">—</span>
        const tel = c.cellPhone ?? c.officePhone
        return (
          <span className="ct-contact">
            {c.workEmail && (
              <a href={`mailto:${c.workEmail}`} onClick={(e) => e.stopPropagation()}>
                {c.workEmail}
              </a>
            )}
            {tel && (
              <a
                href={`tel:${tel.replace(/[^+\d]/g, '')}`}
                className="ct-contact-phone"
                onClick={(e) => e.stopPropagation()}
              >
                {tel}
              </a>
            )}
          </span>
        )
      },
    },
  ]

  return (
    <div className="contacts-page">
      <PageHeader
        eyebrow="Relationships"
        title={
          <>
            All <b>Contacts</b>
          </>
        }
        sub="Investors, third-party clients and brokers across every relationship track. WPI colleagues live under WPI Team."
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="default" icon={<RefreshCw size={15} />} onClick={() => setSyncMineOpen(true)}>
              Sync my contacts
            </Button>
            {/* Baseline crm.use: the people who notice a wrong number are the
                ones using it, and most of them are not admins. */}
            <Button variant="default" icon={<Wrench size={15} />} onClick={() => setCorrectionsOpen(true)}>
              Data corrections
            </Button>
            {canMerge && (
              <Button variant="default" icon={<GitMerge size={15} />} onClick={() => setMergeOpen(true)}>
                Merge duplicates
              </Button>
            )}
            {canManage && (
              <Button variant="default" icon={<RefreshCw size={15} className={syncing ? 'spin' : undefined} />} disabled={syncing} onClick={() => void runDirectorySync()}>
                {syncing ? 'Syncing…' : 'Sync WPI directory'}
              </Button>
            )}
          </div>
        }
      />
      {syncMsg && <div className="contacts-syncmsg">{syncMsg}</div>}
      <ListToolbar
        views={canManage ? [...VIEWS, ARCHIVED_VIEW] : VIEWS}
        activeView={view}
        onView={setView}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search name, title, or account…"
        count={rows.length}
        onAdd={() => setAdding(true)}
        addLabel="Add Contact"
        onExport={() => {}}
        viewMode={viewMode}
        onViewMode={setViewMode}
      />
      {viewMode === 'cards' ? (
        rows.length === 0 ? (
          <EmptyState
            icon={<Users size={36} />}
            title="No contacts match this view."
            hint="Adjust your search or switch views to see more relationships."
          />
        ) : (
          <div className="list-card-grid">
            {rows.map((c) => {
              const acct = accountById(data, c.accountId) as Account | undefined
              const tel = c.cellPhone ?? c.officePhone
              return (
                <Card
                  key={c.id}
                  className="ct-card"
                  onClick={() => nav(`/contacts/${c.id}`)}
                >
                  <div className="ct-card-head">
                    <Avatar name={contactName(c)} size={40} tone={c.isWpi ? 'slate' : 'navy'} />
                    <div className="ct-card-id">
                      <Link
                        to={`/contacts/${c.id}`}
                        className="ct-card-name truncate"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {contactName(c)}
                      </Link>
                      {c.title ? (
                        <span className="ct-card-title truncate">{c.title}</span>
                      ) : (
                        <span className="ct-card-title dim">—</span>
                      )}
                      {acct && (
                        <Link
                          to={`/accounts/${acct.id}`}
                          className="ct-card-acct truncate"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {acct.name}
                        </Link>
                      )}
                    </div>
                  </div>

                  <div className="ct-card-badges">
                    {c.type && (
                      <Badge tone={TYPE_TONE[c.type] ?? 'neutral'} variant="soft">
                        {c.type}
                      </Badge>
                    )}
                    {c.status && c.status !== c.type && (
                      <Badge tone="neutral" variant="outline">
                        {c.status}
                      </Badge>
                    )}
                    {c.stance && <StanceBadge stance={c.stance} />}
                    {c.influence && (
                      <Badge tone={INFLUENCE_TONE[c.influence]} variant="outline">
                        {c.influence} influence
                      </Badge>
                    )}
                  </div>

                  {(c.workEmail || tel) && (
                    <div className="ct-card-contact">
                      {c.workEmail && (
                        <a
                          href={`mailto:${c.workEmail}`}
                          className="ct-card-contact-line truncate"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Mail size={13} />
                          {c.workEmail}
                        </a>
                      )}
                      {tel && (
                        <a
                          href={telHref(tel)}
                          className="ct-card-contact-line ct-card-contact-phone"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Phone size={13} />
                          {tel}
                        </a>
                      )}
                    </div>
                  )}
                </Card>
              )
            })}
          </div>
        )
      ) : (
        <DataTable<Contact>
          columns={columns}
          rows={rows}
          getRowKey={(c) => c.id}
          onRowClick={(c) => nav(`/contacts/${c.id}`)}
          initialSort={{ key: 'name', dir: 'asc' }}
          emptyText="No contacts match this view."
        />
      )}
      <ContactForm open={adding} onClose={() => setAdding(false)} />
      <SyncContactsModal open={syncMineOpen} onClose={() => setSyncMineOpen(false)} contacts={data.contacts} />
      {canMerge && <MergeDuplicatesModal open={mergeOpen} onClose={() => setMergeOpen(false)} />}
      <DataCorrectionsModal open={correctionsOpen} onClose={() => setCorrectionsOpen(false)} />
    </div>
  )
}
